import { useState } from "react";
import { Plus, X } from "lucide-react";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";

/**
 * Reusable Tag component for showing tags
 *
 * @param {string} text - Text for the input
 * @param {string} color - Color for the label(e.g. from-purple-500 to-purple-700)
 * @param {Function} onRemove - Callback to remove value
 */
export const Tag = ({ text, onRemove, color = "gray" }) => {
  // Tones, not raw palette values: these chips land on a light screen as often
  // as a dark one, and `themes/styles/tones.js` is where the contrast for each
  // fg-on-soft pair was measured. Every value there clears 4.5:1 in both themes.
  const colors = {
    gray: "bg-tone-slate-soft text-tone-slate",
    blue: "bg-tone-blue-soft text-tone-blue",
    purple: "bg-tone-purple-soft text-tone-purple",
    orange: "bg-tone-orange-soft text-tone-orange",
    green: "bg-tone-emerald-soft text-tone-emerald",
  };

  return (
    <span title={text} className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs max-w-[160px] ${colors[color]}`}>
      <span className="truncate">{text}</span>
      {onRemove && (
        <Button type="button" aria-label={`Remove ${text}`} onClick={onRemove} className="flex-shrink-0 hover:opacity-70 p-0">
          <X size={12} />
        </Button>
      )}
    </span>
  );
};

/**
 * Reusable input component for adding/removing tags
 *
 * @param {Array} values - Input value
 * @param {Function} onChange - Input onChange handler
 * @param {string} placeholder - Placeholder text for input
 */
const TagInput = ({ values = [], onChange, placeholder }) => {
  const [input, setInput] = useState("");

  const add = () => {
    const v = input.trim();
    if (v && !values.includes(v)) {
      onChange([...values, v]);
      setInput("");
    }
  };

  const remove = (index) => {
    onChange(values.filter((_, i) => i !== index));
  };

  return (
    <div>
      <div className="mb-2">
        <CollapsibleTagList values={values} onRemove={remove} />
      </div>

      <div className="flex gap-2">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
          placeholder={placeholder}
          className="flex-1 px-4 py-2 text-sm rounded-md bg-background-secondary border border-border-primary"
        />

        <Button type="button" onClick={add} className="w-10 h-10 flex items-center justify-center bg-background-tertiary rounded-full">
          <Plus size={16} />
        </Button>
      </div>
    </div>
  );
};

export default TagInput;
